/**
 * Resignation of Secretary (M32107) mapper — AOD(changeOfficerMapper)와 동일한
 * snake_case / boolean / signers 배열 규칙으로 Word [[변수]] 에 맞춤.
 *
 * prepare-documents 는 formData 키 = docx [[placeholder]] exact match (대소문자 구분).
 * DATA_JSON 에는 이 formData 가 그대로 저장된다.
 */

export type ResignGoverningBasis = 'constitution' | 'articles' | 'third_schedule';
export type ResignDirectorMode = 'sole' | 'multiple';

export interface ResignSecretaryRow {
  id: number;
  fullName: string;
  icPassportNo: string;
  residentialAddress: string;
  dateOfCessation: string;
  dateOfAppointment: string;
  licenseType: string;
  licenseNumber: string;
  ssmPcNo: string;
  reason: string;
}

export interface ResignMapperInput {
  values: Record<string, string>;
  rows: number[];
  reasons?: Record<number | string, string>;
  directorMode?: ResignDirectorMode;
  basis?: ResignGoverningBasis;
  signers?: any[];
  /** 특정 secretary 의 Section 58I 서명자 (있으면 board signers 대신 사용) */
  signersForDoc?: any[];
  /** per-secretary 문서일 때 대상 secretary id */
  focusRowId?: number | null;
}

function trim(v: any): string {
  return String(v ?? '').trim();
}

/** UI 라벨 → 정규화 키 (공백/하이픈/대소문자 무시) */
function normalizeReasonKey(reason: string): string {
  return trim(reason).toLowerCase().replace(/[\s\-]+/g, '_');
}

type ReasonId =
  | 'resignation'
  | 'expiry_of_term'
  | 'removal'
  | 'vacate_office'
  | 'deceased';

function resolveReasonId(reason: string): ReasonId | '' {
  const norm = normalizeReasonKey(reason);
  if (norm === 'resignation') return 'resignation';
  if (norm === 'expiry_of_term' || norm === 'expiryofterm') return 'expiry_of_term';
  if (norm === 'removal') return 'removal';
  if (norm === 'vacate_office' || norm === 'vacateoffice') return 'vacate_office';
  if (norm === 'deceased' || norm === 'death') return 'deceased';
  return '';
}

/**
 * Reasons for cessation Word 바인딩.
 *  - reason_* / resignation 등 → ☒·☐ (표에서 [[reason_resignation]] 직접 치환)
 *  - is_reason_* / is_* → boolean ([[if is_reason_resignation]]…[[endif]])
 */
function reasonFlags(reason: string): Record<string, any> {
  const selected = resolveReasonId(reason);
  const defs: Array<{ id: ReasonId; boolKeys: string[]; glyphKeys: string[] }> = [
    {
      id: 'resignation',
      boolKeys: ['is_resignation', 'is_reason_resignation', 'reasonResignation'],
      glyphKeys: ['reason_resignation', 'resignation', 'cb_resignation', 'resignation_check', 'reason_resignation_box'],
    },
    {
      id: 'expiry_of_term',
      boolKeys: ['is_expiry_of_term', 'is_reason_expiry_of_term', 'reasonExpiryOfTerm'],
      glyphKeys: ['reason_expiry_of_term', 'expiry_of_term', 'expiryofterm', 'cb_expiry_of_term', 'expiry_of_term_check', 'reason_expiry_of_term_box'],
    },
    {
      id: 'removal',
      boolKeys: ['is_removal', 'is_reason_removal', 'reasonRemoval'],
      glyphKeys: ['reason_removal', 'removal', 'cb_removal', 'removal_check', 'reason_removal_box'],
    },
    {
      id: 'vacate_office',
      boolKeys: ['is_vacate_office', 'is_reason_vacate_office', 'reasonVacateOffice'],
      glyphKeys: ['reason_vacate_office', 'vacate_office', 'cb_vacate_office', 'vacate_office_check', 'reason_vacate_office_box'],
    },
    {
      id: 'deceased',
      boolKeys: ['is_deceased', 'is_death', 'is_reason_deceased', 'reasonDeceased'],
      glyphKeys: ['reason_deceased', 'deceased', 'death', 'cb_deceased', 'deceased_check', 'reason_deceased_box'],
    },
  ];

  const out: Record<string, any> = {};
  for (const d of defs) {
    const on = selected === d.id;
    for (const k of d.boolKeys) out[k] = on;
    for (const k of d.glyphKeys) out[k] = on ? '☒' : '☐';
  }
  return out;
}

function buildSecretaryFlat(prefix: string, row: ResignSecretaryRow): Record<string, any> {
  return {
    [`${prefix}_full_name`]: row.fullName,
    [`${prefix}_name`]: row.fullName,
    [`${prefix}_ic_passport_no`]: row.icPassportNo,
    [`${prefix}_nric_or_passport_number`]: row.icPassportNo,
    [`${prefix}_nric_or_passport`]: row.icPassportNo,
    [`${prefix}_residential_address`]: row.residentialAddress,
    [`${prefix}_address`]: row.residentialAddress,
    [`${prefix}_date_of_cessation`]: row.dateOfCessation,
    [`${prefix}_cessation_date`]: row.dateOfCessation,
    [`${prefix}_date_of_appointment`]: row.dateOfAppointment,
    [`${prefix}_appointment_date`]: row.dateOfAppointment,
    [`${prefix}_license_type`]: row.licenseType,
    [`${prefix}_license_number`]: row.licenseNumber,
    [`${prefix}_license_no`]: row.licenseNumber,
    [`${prefix}_ssm_pc_no`]: row.ssmPcNo,
    // Word 템플릿 정식 키 (Appointment of Secretary 와 동일 네이밍)
    [`${prefix}_ssm_practising_certificate_number`]: row.ssmPcNo,
    [`${prefix}_ssm_practising_certificate_no`]: row.ssmPcNo,
    [`${prefix}_practising_certificate_number`]: row.ssmPcNo,
    [`${prefix}_reason`]: row.reason,
  };
}

/** Letter / 58I 템플릿: [[resigning_secretary_1_full_name]] 등 번호 prefix */
function buildNumberedSecretaryAliases(
  row: ResignSecretaryRow,
  ordinal: number,
): Record<string, any> {
  const n = Math.max(1, ordinal);
  return {
    ...buildSecretaryFlat(`resigning_secretary_${n}`, row),
    ...buildSecretaryFlat(`resign_secretary_${n}`, row),
    ...buildSecretaryFlat(`secretary_${n}`, row),
    ...buildSecretaryFlat(`sec_${n}`, row),
  };
}

function collectRows(input: ResignMapperInput): ResignSecretaryRow[] {
  const ids = Array.isArray(input.rows) && input.rows.length ? input.rows : [1];
  return ids.map((id) => ({
    id,
    fullName: trim(input.values[`sec${id}_full_name`]),
    icPassportNo: trim(input.values[`sec${id}_ic_passport_no`]),
    residentialAddress: trim(input.values[`sec${id}_residential_address`]),
    dateOfCessation: trim(input.values[`sec${id}_date_of_cessation`]),
    dateOfAppointment: trim(input.values[`sec${id}_date_of_appointment`]),
    licenseType: trim(input.values[`sec${id}_license_type`]),
    licenseNumber: trim(input.values[`sec${id}_license_number`]),
    ssmPcNo: trim(input.values[`sec${id}_ssm_pc_no`]),
    reason:
      trim(
        input.reasons?.[id]
          || input.reasons?.[String(id)]
          || input.values[`sec${id}_reason`]
          || input.values[`sec${id}_reason_for_cessation`]
          || '',
      ) || 'Resignation',
  }));
}

function extractOldRegistrationNo(raw: string): string {
  const m = trim(raw).match(/\(([^)]+)\)\s*$/);
  return m ? trim(m[1]) : '';
}

/**
 * 공통 + DCR / Letter / 58I 공통으로 쓸 formData.
 * focusRowId 가 있으면 그 secretary 를 sec_* / resign_secretary_* 단일 placeholder 로 올린다.
 */
export function buildResignSecretaryFormData(input: ResignMapperInput): Record<string, any> {
  const v = input.values || {};
  const companyName = trim(v.company_name);
  const companyRegistrationNoRaw = trim(v.company_registration_no);
  // 문서에는 "신 (구)" 표기일 수 있음 → 순수 등록번호 / 구번호 분리
  const companyRegNoPure =
    companyRegistrationNoRaw.replace(/\s*\(.*\)\s*$/, '').trim() || companyRegistrationNoRaw;
  const oldRegistrationNo = extractOldRegistrationNo(companyRegistrationNoRaw);
  const companyAddress = trim(v.company_address);
  const constitutionClauseNumber = trim(v.constitution_clause_number);
  const resolutionDate = trim(v.resolution_date);
  const letterDate = trim(v.letter_date) || resolutionDate;

  const govType: ResignGoverningBasis = input.basis || 'constitution';
  const directorMode: ResignDirectorMode = input.directorMode || 'sole';
  const isSoleCompany = directorMode === 'sole';

  const allRows = collectRows(input);
  const focusId = input.focusRowId != null ? Number(input.focusRowId) : null;
  const focusRow =
    (focusId != null ? allRows.find((r) => r.id === focusId) : null) || allRows[0] || {
      id: 1,
      fullName: '',
      icPassportNo: '',
      residentialAddress: '',
      dateOfCessation: '',
      dateOfAppointment: '',
      licenseType: '',
      licenseNumber: '',
      ssmPcNo: '',
      reason: 'Resignation',
    };

  const resolutionBasisClause =
    govType === 'third_schedule'
      ? 'the Third Schedule of the Companies Act 2016'
      : govType === 'articles'
        ? constitutionClauseNumber
          ? `Article ${constitutionClauseNumber} of the Articles of Association`
          : 'the Articles of Association'
        : constitutionClauseNumber
          ? `Clause ${constitutionClauseNumber} of the Constitution`
          : 'the Constitution';

  // DCR 반복부 — 템플릿 [[if resigning_secretaries]] … [[/if resigning_secretaries]]
  //   내부: [[secretary_full_name]], [[secretary_license_type]],
  //         [[secretary_ssm_practising_certificate_number]] 등
  const resigningLoop = allRows.map((row) => ({
    ...buildSecretaryFlat('secretary', row),
    ...buildSecretaryFlat('sec', row),
    ...buildSecretaryFlat('resign_secretary', row),
    ...buildSecretaryFlat('resigning_secretary', row),
    secretary_full_name: row.fullName,
    secretary_name: row.fullName,
    secretary_nric_or_passport_number: row.icPassportNo,
    secretary_license_type: row.licenseType,
    secretary_license_number: row.licenseNumber,
    secretary_ssm_pc_no: row.ssmPcNo,
    secretary_ssm_practising_certificate_number: row.ssmPcNo,
    secretary_ssm_practising_certificate_no: row.ssmPcNo,
    full_name: row.fullName,
    name: row.fullName,
    ic_passport_no: row.icPassportNo,
    nric_or_passport: row.icPassportNo,
    residential_address: row.residentialAddress,
    date_of_cessation: row.dateOfCessation,
    cessation_date: row.dateOfCessation,
    date_of_appointment: row.dateOfAppointment,
    appointment_date: row.dateOfAppointment,
    license_type: row.licenseType,
    license_number: row.licenseNumber,
    ssm_pc_no: row.ssmPcNo,
    ssm_practising_certificate_number: row.ssmPcNo,
    reason: row.reason,
    reason_for_cessation: row.reason,
    ...reasonFlags(row.reason),
  }));

  // DCR 비루프 템플릿용 — resigning_secretary_1_* / _2_* flat
  const numberedFlat: Record<string, any> = {};
  allRows.forEach((row, idx) => {
    Object.assign(numberedFlat, buildNumberedSecretaryAliases(row, idx + 1));
  });

  const multipleResigning = allRows.length > 1;
  const singleResigning = !multipleResigning;

  // 1) UI 원본 키(sec1_* 등)를 먼저 넣고
  // 2) 아래 정규화 키가 덮어쓰게 함 — company_registration_no "신 (구)" 가 순수값을 덮지 않도록.
  const rawFilled = Object.fromEntries(
    Object.entries(v).filter(([k, val]) => k && val != null && String(val).trim() !== ''),
  );

  // Letter / 58I 는 문서당 1명 → 항상 resigning_secretary_1_* 로 focus 행 바인딩
  //   (템플릿 placeholder 가 _1_ 고정)
  const focusNumbered = buildNumberedSecretaryAliases(focusRow, 1);

  const out: Record<string, any> = {
    ...rawFilled,
    ...numberedFlat,
    ...focusNumbered,

    // ── 회사 ──
    company_name: companyName,
    company_registration_no: companyRegNoPure,
    registration_number: companyRegNoPure,
    old_registration_no: oldRegistrationNo || trim(v.old_registration_no) || '',
    company_address: companyAddress,
    registered_address: companyAddress,
    company_registered_address: companyAddress,

    // ── 정관 / 결의 근거 ──
    constitution_clause_number: constitutionClauseNumber,
    constitution_clause_no: constitutionClauseNumber,
    resolution_basis_clause: resolutionBasisClause,
    governing_document_clause: resolutionBasisClause,

    constitution: govType === 'constitution',
    articles: govType === 'articles',
    third_schedule: govType === 'third_schedule',
    resolution_basis_constitution: govType === 'constitution',
    resolution_basis_articles: govType === 'articles',
    resolution_basis_third_schedule: govType === 'third_schedule',

    // 회사 director 수 (결의문 I/We) — UI directorMode
    is_sole_director_company: isSoleCompany,
    is_multiple_director_company: !isSoleCompany,
    single_director_company: isSoleCompany,
    multiple_directors_company: !isSoleCompany,
    single_director: isSoleCompany,
    multiple_directors: !isSoleCompany,
    // camelCase 폴백 (구 mapper)
    soleDirector: isSoleCompany,
    multipleDirectors: !isSoleCompany,
    basisConstitution: govType === 'constitution',
    basisArticles: govType === 'articles',
    basisThirdSchedule: govType === 'third_schedule',
    directorMode,
    basis: govType,

    // 사임 secretary 단/복수 — 템플릿 정식 키 (제목 RESIGNATION OF SECRETARY/IES, is/are)
    //   [[if resign_single_secretary]]Secretary[[endif]]
    //   [[if resign_multiple_secretaries]]Secretaries[[endif]]
    resign_single_secretary: singleResigning,
    resign_multiple_secretaries: multipleResigning,
    // 별칭
    resigning_single: singleResigning,
    resigning_multiple: multipleResigning,
    resigning_single_secretary: singleResigning,
    resigning_multiple_secretaries: multipleResigning,

    // ── 날짜 ──
    resolution_date: resolutionDate,
    letter_date: letterDate,
    resignation_date: focusRow.dateOfCessation || letterDate,
    date_of_declaration: resolutionDate,
    date_of_cessation: focusRow.dateOfCessation,
    cessation_date: focusRow.dateOfCessation,

    // ── 반복부 (DCR) — 템플릿 [[if resigning_secretaries]] ──
    resigning_secretaries: resigningLoop,
    resign_secretaries: resigningLoop,
    Resigning_secretaries: resigningLoop,

    // ── 단일 secretary placeholder (Letter / 58I / DCR 1명) ──
    ...buildSecretaryFlat('secretary', focusRow),
    ...buildSecretaryFlat('sec', focusRow),
    ...buildSecretaryFlat('resign_secretary', focusRow),
    ...buildSecretaryFlat('resigning_secretary', focusRow),
    secretary_full_name: focusRow.fullName,
    secretary_nric_or_passport_number: focusRow.icPassportNo,
    secretary_license_type: focusRow.licenseType,
    secretary_license_number: focusRow.licenseNumber,
    secretary_ssm_pc_no: focusRow.ssmPcNo,
    secretary_ssm_practising_certificate_number: focusRow.ssmPcNo,
    secretary_ssm_practising_certificate_no: focusRow.ssmPcNo,
    ssm_practising_certificate_number: focusRow.ssmPcNo,
    SECRETARY_CERTIFICATE_NO: focusRow.ssmPcNo,
    resign_secretary_name: focusRow.fullName,
    resign_secretary_address: focusRow.residentialAddress,
    secretary_name: focusRow.fullName,
    officer_name: focusRow.fullName,
    reason: focusRow.reason,
    reason_for_cessation: focusRow.reason,
    reasons_for_cessation: focusRow.reason,
    cessation_reason: focusRow.reason,
    ...reasonFlags(focusRow.reason),

    // UPPER_CASE alias
    COMPANY_NAME: companyName,
    COMPANY_REGISTRATION_NO: companyRegNoPure,
    COMPANY_ADDRESS: companyAddress,
    RESOLUTION_DATE: resolutionDate,
    LETTER_DATE: letterDate,
    RESIGN_SECRETARY_NAME: focusRow.fullName,
    DATE_OF_CESSATION: focusRow.dateOfCessation,

    _signerLayout: 'box',
  };

  // signers — 배열로 (AOD와 동일). JSON 문자열이면 템플릿/백엔드가 깨질 수 있음.
  const signersSrc = Array.isArray(input.signersForDoc) && input.signersForDoc.length
    ? input.signersForDoc
    : Array.isArray(input.signers)
      ? input.signers
      : [];
  if (signersSrc.length > 0) {
    out.signers = signersSrc.map((s: any) => ({ ...s, _signerLayout: 'box' }));
  }

  return out;
}

/** @deprecated — 패널이 ResignMapperInput 형태로 호출하도록 교체됨. 호환용 래퍼. */
export function buildResignSecretaryFormDataLegacy(
  values: Record<string, string>,
  local: {
    directorMode?: string;
    basis?: string;
    reasons?: Record<number | string, string>;
    signers?: any[];
    signers58?: Record<string, any[]>;
    rows?: number[];
  },
): Record<string, any> {
  return buildResignSecretaryFormData({
    values,
    rows: local.rows || [1],
    reasons: local.reasons,
    directorMode: (local.directorMode as ResignDirectorMode) || 'sole',
    basis: (local.basis as ResignGoverningBasis) || 'constitution',
    signers: local.signers,
  });
}
